import { cloneDeep, set } from 'lodash-es'

import type { FlagInput, FlagsWithOptionalNetwork } from './flags.types'
import { Feature, FlagType, getFlags, Untracked } from './flags.types'

// We configure some app-level flags in flags.ts, but may need to override these on
// a per-user basis. This only applies to two users currently. Our local user (the
// one used for development and CI), and the one we use for our user testing
export const mergeConfigFlagsWithUserMetadata = <F extends FlagInput>(flags: F, metadata: Maybe<UserMetadata[]>) => {
  // Filter any metadata that looks like a feature flag
  const userFlags = (metadata || []).filter(({ name }) => {
    return (name || '').includes('feature.') || (name || '').includes('automation.') || (name || '').includes('club.')
  })

  // Override any flags set at a user-level
  const flagsCopy = cloneDeep(flags)
  return userFlags.reduce((acc, flag) => {
    const { name, string_value, bool_value } = flag
    const enabled = string_value === 'true' || bool_value === true
    const disabled = string_value === 'false' || bool_value === false
    if (!name) return acc
    if (enabled) return set(acc, name, true)
    if (disabled) return set(acc, name, false)
    return acc
  }, flagsCopy)
}

// Temporary pre-auth QA override mechanism: reads flag overrides a tester has set before
// organisation/user metadata exists to calculate flags from. Never throws — a missing or
// malformed value is treated as no overrides.
export const readFlagOverrides = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem('acre_flag_overrides')
    const parsed = raw ? JSON.parse(raw) : {}
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

// Temporary pre-auth QA override mechanism: layers dot-path overrides onto a set of flags
// without mutating the input.
export const applyFlagOverrides = <F extends FlagInput>(flags: F, overrides: Record<string, boolean>): F => {
  const flagsCopy = cloneDeep(flags)
  return Object.entries(overrides).reduce((acc, [key, value]) => set(acc, key, value), flagsCopy)
}

// List of relevant flag prefixes and categories.(UI cards corelating to this )
// Extend this list when new flag categories or specific flags are introduced.
const RELEVANT_FLAG_IDENTIFIERS = ['feature.', 'automation.', 'untracked.', 'club']

// Filter relevant metadata flags
const filterRelevantFlags = (metadata: Metadata[]) => {
  return metadata.filter(({ name }) => {
    return (
      RELEVANT_FLAG_IDENTIFIERS.some((keyword) => (name || '').startsWith(keyword)) ||
      Object.values(Untracked).includes(name as Untracked)
    )
  })
}

export const mergeConfigFlagsWithOrgMetadata = (
  flags: FlagsWithOptionalNetwork,
  metadata: Maybe<Metadata[]>,
): FlagsWithOptionalNetwork => {
  // If metadata is null or undefined, return the original flags
  if (!metadata) {
    return flags
  }

  // Filter out metadata that are relevant to flag settings
  const orgFlags = filterRelevantFlags(metadata)

  // Deep clone the original flags to avoid mutation
  const flagsCopy = cloneDeep(flags)

  // Merge the org flags into the copy of the original flags
  return orgFlags.reduce((acc, { name, bool_value, string_value }) => {
    if (name && bool_value !== null) {
      const enabled = string_value === 'true' || bool_value === true
      const disabled = string_value === 'false' || bool_value === false
      // Check if the flag is one of the untracked flags(ie doesn't have prefix), and if so, place it under the 'untracked' category
      if (Object.values(Untracked).includes(name as Untracked)) {
        name = `untracked.${name}`
      }
      if (enabled) set(acc, name, true)
      if (disabled) set(acc, name, false)
    }
    return acc
  }, flagsCopy)
}

/**
 * Check if a user or org has explicitly enabled or disabled a specific feature flag
 */
export const isFeatureFlagSet = (userOrOrg: Maybe<User> | Maybe<Organisation>, featureFlag: Feature) => {
  if (!userOrOrg) return false

  return (
    userOrOrg?.metadata?.some(
      (meta) =>
        meta.name === `feature.${featureFlag}` &&
        (meta.string_value === 'true' ||
          meta.bool_value === true ||
          meta.string_value === 'false' ||
          meta.bool_value === false),
    ) ?? false
  )
}

export const calculateSurveyingCpFlag = (
  flags: FlagsWithOptionalNetwork,
  parentOrg: Maybe<Organisation>,
  currentOrg: Maybe<Organisation>,
) => {
  const isSurveyingCpFlagSet = [parentOrg, currentOrg].some((org) => isFeatureFlagSet(org, Feature.SurveyingCp))

  // if SurveyingCp flag is set it should override Surveying flag, otherwise use Surveying flag's value
  return isSurveyingCpFlagSet
    ? flags[FlagType.Feature][Feature.SurveyingCp]
    : flags[FlagType.Feature][Feature.Surveying]
}

/**
 * Calculate flags for parent organization only
 * @param parentOrgMetadata - Parent organization metadata
 * @returns Flags with parent organization overrides applied
 */
export const calculateParentOrgFlags = (parentOrgMetadata: Maybe<Metadata[]>) => {
  return mergeConfigFlagsWithOrgMetadata(getFlags(), parentOrgMetadata)
}

/**
 * Calculate flags for current organization only (including user metadata)
 * @param organisationData - Current organization data
 * @param currentUser - Current user data
 * @param baseFlags - Base flags to start from (defaults to global flags)
 * @returns Flags with current organization and user overrides applied
 */
export const calculateCurrentOrgFlags = (
  organisationData: Maybe<Organisation>,
  currentUser?: Maybe<User>,
  baseFlags: FlagsWithOptionalNetwork = getFlags(),
) => {
  const currentOrganisationMetadata = organisationData ? organisationData.metadata : []

  const { metadata: userMetadata } = currentUser || { metadata: [] }

  const isAR = organisationData?.type === 'AR' && organisationData?.representative_of !== organisationData?.id

  // Apply AR logic to remove network feature flag if organization is AR
  const { network, ...featureWithoutNetwork } = baseFlags.feature || {}
  const flagsWithARLogic = {
    ...baseFlags,
    feature: {
      ...featureWithoutNetwork,
      ...(isAR ? {} : { network }), // Only merge network feature flag if not AR
    },
  }

  const overriddenFlagsOrg = mergeConfigFlagsWithOrgMetadata(flagsWithARLogic, currentOrganisationMetadata)

  // calculate SurveyingCp flag based on Surveying and SurveyingCp flag value
  overriddenFlagsOrg[FlagType.Feature][Feature.SurveyingCp] = calculateSurveyingCpFlag(
    overriddenFlagsOrg,
    organisationData?.representative_of_org,
    organisationData,
  )

  const overriddenFlagsUser = mergeConfigFlagsWithUserMetadata(overriddenFlagsOrg, userMetadata)

  // EidvDonors flag should only take its value from DA org metadata, ignoring AR/user overrides
  overriddenFlagsUser[FlagType.Feature][Feature.EidvDonors] = baseFlags[FlagType.Feature][Feature.EidvDonors]

  return overriddenFlagsUser
}

export const calculateFlags = (organisationData: Maybe<Organisation>, currentUser?: Maybe<User>) => {
  const parentOrganisationMetadata = organisationData?.representative_of_org
    ? organisationData.representative_of_org.metadata
    : []

  // Calculate parent org flags first
  const parentFlags = calculateParentOrgFlags(parentOrganisationMetadata)

  // Then apply current org and user flags using parent flags as base
  return calculateCurrentOrgFlags(organisationData, currentUser, parentFlags)
}

/**
 * Check if the user or org has a specific feature flag enabled
 */
export const hasFeatureFlag = (userOrOrg: Maybe<User> | Maybe<Organisation>, featureFlag: Feature) => {
  if (!userOrOrg) return false

  return (
    userOrOrg?.metadata?.some(
      (meta) => meta.name === `feature.${featureFlag}` && (meta.string_value === 'true' || meta.bool_value === true),
    ) ?? false
  )
}
